import React, { useState } from "react";
import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import Section from "@cs/components/src/Section";
import Button from "@cs/components/src/Button";
import Input from "@cs/components/src/Input";
import ApiService from "../../services/ApiService";
import Banner from "@cs/components/src/Banner";
import { exchangeLoginCode } from "../../services/sessionExchange";
import { Creds, saveLaunchCreds } from "../../services/launchCreds";
import { ArrowLeft } from "react-feather";

const Wrapper = styled.div`
  max-width: 500px;
  width: 90%;
  margin: auto;
  margin-top: 50px;
`;

const Message = styled.div`
  font-size: 15px;
  margin-bottom: 15px;
  text-align: center;
  color: var(--gray300);
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 10px;
`;

const loginError = new URLSearchParams(window.location.search).get(
  "login_error"
);

/**
 * Structured MFA-required response the backend returns when iconik signals that OTP fields are
 * needed (same shape/pattern as organizationConfig.ts's `/launch` route uses for the "Open in
 * Iconik" cross-org launch flow — SessionService.passwordLogin's phase 1).
 */
interface MfaRequiredData {
  mfaRequired: true;
  otpType: "TOTP" | "MAIL_2SV";
}

interface LoginCodeData {
  loginCode: string;
}

/** Human-readable prompt copy for each OTP method. */
function otpLabel(otpType: "TOTP" | "MAIL_2SV"): string {
  if (otpType === "MAIL_2SV") {
    return "Enter the code we emailed you";
  }
  return "Enter the code from your authenticator app";
}

/**
 * Sign-in screen — email + password + (when the account has 2FA enabled) a verification code,
 * calling the backend's direct iconik login (POST /api/session/password/login), which is gated
 * to admins in CineSys's own iconik system domain exactly like the (currently dormant, not
 * UI-surfaced) OAuth flow was — see SessionService.passwordLogin's doc comment.
 */
const RequestPinScreen = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /** null = step 1 (creds); non-null = step 2 (code), carrying the OTP method to prompt for. */
  const [otpType, setOtpType] = useState<"TOTP" | "MAIL_2SV" | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * Redeem the one-time login code — the backend sets the httpOnly session cookie on this same
   * response — then cache the just-used creds in memory so a subsequent "Open in Iconik" launch
   * (SelectOrganization.tsx) doesn't re-prompt — one login covers both. Uses the freshly-minted
   * session's own expiry (from the exchange response, since the cookie itself is JS-invisible)
   * so cached creds never outlive the session that authorized them.
   */
  const completeLogin = async (loginCode: string, creds: Creds) => {
    const session = await exchangeLoginCode(loginCode);
    const expiresAt = session.expires ?? Math.floor(Date.now() / 1000) + 3600;
    saveLaunchCreds(creds, expiresAt);
    queryClient.invalidateQueries(["session"]);
    navigate("/", { replace: true });
  };

  const submitCredentials = async () => {
    if (!email || !password || loading) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await ApiService.fetch<{
        data: MfaRequiredData | LoginCodeData;
      }>("POST", "/api/session/password/login", { email, password });

      if ((data as MfaRequiredData).mfaRequired) {
        setOtp("");
        setOtpType((data as MfaRequiredData).otpType);
        return;
      }

      await completeLogin((data as LoginCodeData).loginCode, {
        email,
        password,
      });
    } catch (e: any) {
      // Same friendly-message convention as SelectOrganization.tsx's launchPhase1 for the
      // identical backend message.
      if (e.message === "Invalid iconik credentials") {
        setError("Wrong email or password.");
      } else {
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async () => {
    if (!otp.trim() || loading) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await ApiService.fetch<{
        data: MfaRequiredData | LoginCodeData;
      }>("POST", "/api/session/password/login", {
        email,
        password,
        otpType,
        otp: otp.replace(/\s/g, ""),
      });

      // Guard: phase 2 should always return a loginCode, never mfaRequired again.
      if ((data as MfaRequiredData).mfaRequired) {
        setError("Something went wrong. Please try again.");
        return;
      }

      await completeLogin((data as LoginCodeData).loginCode, {
        email,
        password,
      });
    } catch (e: any) {
      if (e.message === "Invalid iconik credentials") {
        // Password changed / became invalid between step 1 and step 2 — go back.
        setOtpType(null);
        setOtp("");
        setError("Session expired — please sign in again.");
      } else if (e.message === "Invalid or expired verification code") {
        setOtp("");
        setError("Invalid or expired code — enter a new one.");
      } else {
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setOtpType(null);
    setOtp("");
    setError(null);
  };

  return (
    <Wrapper>
      <Section title="Iconik Manager" pad>
        {loginError && <Banner error={loginError} padBottom />}
        {otpType === null ? (
          <>
            <Input
              label="Email"
              value={email}
              onChange={setEmail}
              onSubmit={submitCredentials}
              autoFocus
              autoComplete="email"
              name="email"
              id="login-email"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              onSubmit={submitCredentials}
              autoComplete="current-password"
              name="password"
              id="login-password"
            />
            <Button
              label="Sign in"
              type="success"
              onClick={submitCredentials}
              loading={loading}
              disabled={!email || !password || loading}
            />
          </>
        ) : (
          <>
            <Message>{otpLabel(otpType)}</Message>
            <Input
              label="Verification code"
              placeholder="6-digit code"
              value={otp}
              onChange={setOtp}
              onSubmit={submitOtp}
              autoFocus
              autoComplete="one-time-code"
              name="one-time-code"
              id="login-otp"
            />
            <Button
              label="Verify"
              type="success"
              onClick={submitOtp}
              loading={loading}
              disabled={!otp.trim() || loading}
            />
            <BackLink>
              <Button
                label="Back"
                type="default"
                borderless
                onClick={backToCredentials}
                disabled={loading}
                icon={ArrowLeft}
              />
            </BackLink>
          </>
        )}
        {error && <Banner error={error} padTop />}
      </Section>
    </Wrapper>
  );
};

export default RequestPinScreen;
